import { forwardRef, useId } from 'react';

/**
 * A binary on/off control, styled as a track + thumb but backed by a real
 * `<input type="checkbox" role="switch">` for native keyboard and screen
 * reader behaviour (spec §7.1). The input is visually hidden — only the
 * `.etch-switch__track`/`__thumb` are shown — while staying focusable and in
 * the accessibility tree.
 *
 * @param {object} props
 * @param {string} [props.id]
 * @param {import('react').ReactNode} [props.label] - visible label rendered
 *   after the track; for icon-only switches pass an `aria-label` instead and
 *   omit `label`.
 * @param {import('react').ReactNode} [props.hint]
 * @param {import('react').ReactNode} [props.error] - takes precedence over
 *   `hint`, same aria-invalid + aria-describedby contract as the text fields.
 *   Rendered as a sibling of the `<label>` (a Fragment, not a wrapping element)
 *   so Switch keeps its original single-element footprint when unused.
 * @param {string} [props.className]
 */
export const Switch = forwardRef(function Switch({ id, label, hint, error, className = '', ...rest }, ref) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <>
      <label htmlFor={inputId} className={`etch-switch ${className}`.trim()}>
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          role="switch"
          className="etch-visually-hidden"
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        <span className="etch-switch__track" aria-hidden="true">
          <span className="etch-switch__thumb" />
        </span>
        {label && <span>{label}</span>}
      </label>
      {error ? (
        <span id={errorId} className="etch-field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span id={hintId} className="etch-field__hint">
          {hint}
        </span>
      ) : null}
    </>
  );
});

export default Switch;
